/**
 * ThinkingIndicator — feedback honesto enquanto o LeadCopy responde.
 *
 * Como a chamada ao LLM é bloqueante (não streamed), não temos como saber em
 * tempo real o que ele está fazendo. Em vez de teatro com frases cíclicas, a
 * gente:
 *  1) Mostra um pulso visual discreto (3 dots em bounce)
 *  2) Avança através de fases conhecidas (lendo contexto → pensando → gerando)
 *     em ordem linear; após chegar à última, fica nela
 *  3) Mostra o tempo decorrido em segundos a partir de 3s — pra usuário ter
 *     uma medida concreta da espera, não uma promessa vaga
 */

import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";

type Mode = "email" | "flow" | "campaign";

// Fases lineares (não ciclam). Última fase fica visível até a resposta chegar.
const PHASES: Record<Mode, string[]> = {
  email: [
    "Lendo o e-mail e o contexto…",
    "Consultando a voz da marca…",
    "Gerando resposta…",
  ],
  flow: [
    "Lendo o estado do fluxo…",
    "Consultando o contexto da Inteligência…",
    "Gerando resposta…",
  ],
  campaign: [
    "Lendo a estrutura da campanha…",
    "Consultando o contexto e o estilo escolhido…",
    "Gerando resposta…",
  ],
};

// Tempo aproximado pra avançar entre fases (em ms). Linear, sem ciclo.
const PHASE_STEP_MS = 1500;
// A partir de quanto tempo mostramos o contador de segundos
const SHOW_TIMER_AFTER_MS = 3000;

export function ThinkingIndicator({ mode }: { mode: Mode }) {
  const phases = PHASES[mode];
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      setElapsedMs(elapsed);
      // Avança a fase linearmente; trava na última
      const targetIdx = Math.min(Math.floor(elapsed / PHASE_STEP_MS), phases.length - 1);
      setPhaseIdx(targetIdx);
    }, 200);
    return () => clearInterval(tick);
  }, [phases.length]);

  const showTimer = elapsedMs >= SHOW_TIMER_AFTER_MS;
  const seconds = Math.floor(elapsedMs / 1000);

  return (
    <div className="flex items-start gap-2">
      <div className="size-7 shrink-0 mt-0.5 rounded-full bg-primary/10 flex items-center justify-center">
        <Sparkles className="size-3.5 text-primary" />
      </div>
      <div className="rounded-lg bg-muted px-3 py-2 flex items-center gap-2 min-h-[34px]">
        <div className="flex gap-1">
          <span className="size-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "0ms" }} />
          <span className="size-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "150ms" }} />
          <span className="size-1.5 rounded-full bg-muted-foreground/60 animate-bounce" style={{ animationDelay: "300ms" }} />
        </div>
        <span className="text-xs text-muted-foreground">{phases[phaseIdx]}</span>
        {showTimer && (
          <span className="text-[10px] text-muted-foreground/60 tabular-nums ml-auto">{seconds}s</span>
        )}
      </div>
    </div>
  );
}
